import { rand } from '~/utils/random'
/**
 * ⚔️ ELITE MODIFIERS (v5.42, designed with Javi): every elite node rolls
 * ONE modifier at map generation, previewed on the map (overlay glyph
 * bottom-center of the node icon — placeholder emoji until Javi's
 * painted icons land) so the "elite path or not" choice is informed.
 * Eight pressures, each taxing a different resource:
 *
 *   scars      bodies   (A1+ only — needs the injury system running)
 *   grueling   actions  (on-use effects dead from the start)
 *   enraged    time     (waiting missions grow +1 req every 10s)
 *   duelist    comp     (max 2 creatures per mission)
 *   voracious  risk     (failed AND expired missions rise once, +2 req)
 *   banner     order    (until the champion falls, everything else +2)
 *   blackout   info     (win chances hidden — read the radar yourself)
 *   noquarter  triage   (the discard button is disabled)
 *
 * v5.43 (per Javi): no modifier repeats within an act — draws are
 * WITHOUT replacement (rollEliteModifiers).
 */
export type EliteModifierId =
  | 'scars' | 'grueling' | 'enraged' | 'duelist' | 'voracious'
  | 'banner' | 'blackout' | 'noquarter'

export interface EliteModifierDef {
  id: EliteModifierId
  /** Placeholder glyph; swaps for Javi's painted icon (a path renders
   * as an <img>) — same contract as the event OPTION_ICONS. */
  icon: string
  name: string
  blurb: string
}

export const ELITE_MODIFIERS: Record<EliteModifierId, EliteModifierDef> = {
  scars: {
    id: 'scars', icon: '/ui/emods/scars.webp', name: 'Battle Scars',
    blurb: 'Winning here still hurts: every mission WON injures one random creature on the team.',
  },
  grueling: {
    id: 'grueling', icon: '/ui/emods/grueling.webp', name: 'Grueling',
    blurb: 'No tricks on this ground: Surges and on-use skills cannot be armed for the whole node. Raw statlines only.',
  },
  enraged: {
    id: 'enraged', icon: '/ui/emods/enraged.webp', name: 'Enraged',
    blurb: 'Waiting missions GROW: +1 to one requirement every 5 seconds (up to +5). Move fast or pay for it.',
  },
  duelist: {
    id: 'duelist', icon: '/ui/emods/duelist.webp', name: 'Duelist’s Code',
    blurb: 'Missions accept at most TWO creatures, whatever their size. Send your best, not your many.',
  },
  voracious: {
    id: 'voracious', icon: '/ui/emods/voracious.webp', name: 'Voracious',
    blurb: 'Failed and expired missions RISE AGAIN once — as TWO hungrier copies, each at +2 requirement.',
  },
  banner: {
    id: 'banner', icon: '/ui/emods/banner.webp', name: 'Warlord’s Banner',
    blurb: 'The CHAMPION marches in SECOND, another at its heels — and while it stands, every other mission demands +2. Cut down the head and the rest falter.',
  },
  blackout: {
    id: 'blackout', icon: '/ui/emods/blackout.webp', name: 'Blackout',
    blurb: 'Win chances are HIDDEN on this ground — no percentages, no radar. Read the posted demands and trust your build.',
  },
  noquarter: {
    id: 'noquarter', icon: '/ui/emods/noquarter.webp', name: 'No Quarter',
    blurb: 'No discarding here — and an expiry bites HALF AGAIN as hard. No quarter given, none received.',
  },
}

/** Enraged growth cap: a camped mission never grows past this. */
export const ENRAGE_CAP = 5
/** Enraged tick: seconds between +1s (v6.00, per Javi: 10 "too slow"). */
export const ENRAGE_SECONDS = 5
export const VORACIOUS_REQ_BONUS = 2

export const eliteModifier = (id: string | undefined | null): EliteModifierDef | null =>
  id ? ELITE_MODIFIERS[id as EliteModifierId] ?? null : null

/** 🚩 Warlord's Banner: the tax every non-champion mission carries
 * while the champion stands. */
export const BANNER_TAX = 2

/** One modifier per elite node, rolled at map generation. Battle Scars
 * needs the injury system, so below Ascension 1 the deck excludes it. */
export function rollEliteModifier(ascension: number): EliteModifierId {
  return rollEliteModifiers(ascension, 1)[0]!
}

/** ⚔️ v5.43 (per Javi): a whole act's elite modifiers in one draw —
 * WITHOUT replacement, so no modifier repeats within the act (the
 * elite-gauntlet row at A6+ deals a different price at every door).
 * Wraps only if an act ever wants more elites than the deck holds. */
export function rollEliteModifiers(ascension: number, count: number): EliteModifierId[] {
  const pool = (Object.keys(ELITE_MODIFIERS) as EliteModifierId[])
    .filter(id => id !== 'scars' || ascension >= 1)
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!]
  }
  return Array.from({ length: count }, (_, i) => pool[i % pool.length]!)
}
